import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Library, CalendarDays, Sparkles, ChevronRight, Home } from "lucide-react";
import { adminFetch } from "../lib/auth";

interface UserInfo {
  id: string;
  name: string;
}

// Dashboard d'admin : 2 entrées principales.
// - Question Sets : sets ponctuels (générés et partagés via /auto-room?questions=<id>)
// - My Schedule   : calendrier perso (sessions générées par lots, partagé via /auto-room?vitrine=<userId>)
export function AdminDashboard() {
  const [user, setUser] = useState<UserInfo | null>(null);

  useEffect(() => {
    (async () => {
      const res = await adminFetch("/api/admin/me");
      if (!res) return; // session expirée : adminFetch a déjà déclenché la nav vers Authentik
      const data = await res.json();
      setUser({ id: data.id, name: data.name || "Anonymous" });
    })().catch(() => {});
  }, []);

  return (
    <div className="min-h-screen font-kawaii bg-gradient-to-br from-sakura-100 via-lavender-100 to-sky-100 relative overflow-hidden">
      <div className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] rounded-full bg-sakura-200/40 blur-3xl pointer-events-none" />
      <div className="absolute bottom-[-10%] right-[-10%] w-[400px] h-[400px] rounded-full bg-lavender-200/40 blur-3xl pointer-events-none" />

      <div className="relative z-10 max-w-4xl mx-auto p-4 py-10">
        <motion.div
          initial={{ y: -20, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          className="flex items-center justify-between mb-8"
        >
          <nav className="flex items-center gap-1 text-sm font-bold">
            <Link
              to="/"
              className="flex items-center gap-1 text-lavender-400 hover:text-sakura-400 transition-colors"
            >
              <Home className="w-4 h-4" />
              Home
            </Link>
            <ChevronRight className="w-4 h-4 text-lavender-300" />
            <span className="text-sakura-500">Admin</span>
          </nav>
          {user && (
            <span className="text-xs font-semibold text-lavender-400/80">
              Signed in as <span className="text-sakura-500">{user.name}</span>
            </span>
          )}
        </motion.div>

        <motion.div
          initial={{ y: -10, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.05 }}
          className="text-center mb-10"
        >
          <h1 className="text-4xl font-black text-sakura-500 flex items-center justify-center gap-2">
            <Sparkles className="w-8 h-8 text-star" />
            Admin
            <Sparkles className="w-8 h-8 text-star" />
          </h1>
          <p className="text-lavender-400 font-semibold mt-2">
            Create one-shot quizzes or plan a full season.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6">
          <DashCard
            to="/admin/sets"
            icon={<Library className="w-10 h-10" />}
            title="Question Sets"
            description="Generate a one-shot set via LLM, edit it, and share the link."
            cta="Manage sets"
            accent="sakura"
            delay={0.1}
          />
          <DashCard
            to="/admin/schedule"
            icon={<CalendarDays className="w-10 h-10" />}
            title="My Schedule"
            description="Plan a full season (weekly, monthly...) generated by LLM."
            cta="Open schedule"
            accent="lavender"
            delay={0.2}
          />
        </div>
      </div>
    </div>
  );
}

interface DashCardProps {
  to: string;
  icon: React.ReactNode;
  title: string;
  description: string;
  cta: string;
  accent: "sakura" | "lavender";
  delay: number;
}

function DashCard({ to, icon, title, description, cta, accent, delay }: DashCardProps) {
  const gradient =
    accent === "sakura"
      ? "from-sakura-400 to-lavender-400"
      : "from-lavender-400 to-sky-400";
  const iconBg = accent === "sakura" ? "bg-sakura-100 text-sakura-500" : "bg-lavender-100 text-lavender-500";
  return (
    <motion.div
      initial={{ y: 20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ delay, type: "spring", bounce: 0.4 }}
    >
      <Link
        to={to}
        className="block bg-white/85 backdrop-blur-xl rounded-blob shadow-kawaii-lg border-2 border-sakura-200/50 p-7 h-full hover:shadow-kawaii-xl hover:-translate-y-1 transition-all group"
      >
        <div className={`w-16 h-16 rounded-2xl ${iconBg} flex items-center justify-center mb-5 group-hover:scale-110 transition-transform`}>
          {icon}
        </div>
        <h2 className="text-2xl font-black text-gray-700 mb-2">{title}</h2>
        <p className="text-sm text-gray-500 font-semibold leading-relaxed mb-5">
          {description}
        </p>
        <span className={`inline-flex items-center gap-1 px-4 py-2 rounded-full text-white font-black text-sm bg-gradient-to-r ${gradient} shadow-kawaii`}>
          {cta}
          <ChevronRight className="w-4 h-4" />
        </span>
      </Link>
    </motion.div>
  );
}
